import {
  drawSchema,
  notificationPreferenceSchema,
  predictionEvaluationHistoryPageSchema,
  predictionEvaluationPageSchema,
  predictionSchema,
  savedSetSchema,
  type Draw,
  type GameCode,
  type NotificationPreference,
  type Prediction,
  type PredictionEvaluationHistoryPage,
  type PredictionEvaluationPage,
  type SavedSet
} from "@lottolab/domain";
import { z } from "zod";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "";
export const USE_DEMO_DATA = import.meta.env.VITE_USE_DEMO_DATA === "true";

export class ApiRequestError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code = "request_failed"
  ) {
    super(message);
  }
}

export async function apiFetch<T>(
  path: string,
  init: RequestInit = {},
  schema?: z.ZodType<T>
): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has("content-type")) {
    headers.set("content-type", "application/json");
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    credentials: "include",
    headers
  });
  const payload = (await response.json().catch(() => null)) as unknown;

  if (!response.ok) {
    const parsed = z
      .object({
        error: z.object({
          code: z.string().optional(),
          message: z.string()
        })
      })
      .safeParse(payload);
    throw new ApiRequestError(
      parsed.success ? parsed.data.error.message : "Yêu cầu không thành công.",
      response.status,
      parsed.success ? parsed.data.error.code : "request_failed"
    );
  }

  return schema ? schema.parse(payload) : (payload as T);
}

const sessionSchema = z.object({
  user: z.object({
    id: z.string().uuid(),
    email: z.string().email(),
    displayName: z.string().nullable(),
    adultAcknowledged: z.boolean()
  })
});

export type Session = z.infer<typeof sessionSchema>;

export type SignUpInput = {
  email: string;
  password: string;
  displayName: string;
  adultAcknowledged: boolean;
  turnstileToken: string;
};

export type LoginInput = {
  email: string;
  password: string;
  turnstileToken: string;
};

export const authApi = {
  session: () => apiFetch("/api/v1/auth/session", {}, sessionSchema),
  login: (input: LoginInput) =>
    apiFetch(
      "/api/v1/auth/login",
      { method: "POST", body: JSON.stringify(input) },
      sessionSchema
    ),
  signup: (input: SignUpInput) =>
    apiFetch<{ message: string }>(
      "/api/v1/auth/signup",
      { method: "POST", body: JSON.stringify(input) },
      z.object({ message: z.string() })
    ),
  logout: () =>
    apiFetch<{ ok: boolean }>(
      "/api/v1/auth/logout",
      { method: "POST" },
      z.object({ ok: z.boolean() })
    ),
  forgotPassword: (email: string, turnstileToken: string) =>
    apiFetch<{ message: string }>(
      "/api/v1/auth/forgot-password",
      { method: "POST", body: JSON.stringify({ email, turnstileToken }) },
      z.object({ message: z.string() })
    ),
  resetPassword: (password: string, accessToken: string) =>
    apiFetch<{ message: string }>(
      "/api/v1/auth/reset-password",
      { method: "POST", body: JSON.stringify({ password, accessToken }) },
      z.object({ message: z.string() })
    )
};

export const gameApi = {
  currentPrediction: (gameCode: GameCode): Promise<Prediction> =>
    apiFetch(`/api/v1/games/${gameCode}/prediction/current`, {}, predictionSchema),
  draws: (gameCode: GameCode): Promise<Draw[]> =>
    apiFetch(
      `/api/v1/games/${gameCode}/draws?limit=8`,
      {},
      z.array(drawSchema)
    ),
  evaluatedPredictions: (
    gameCode: GameCode,
    options: { limit?: number; offset?: number } = {}
  ): Promise<PredictionEvaluationPage> => {
    const query = new URLSearchParams({
      limit: String(options.limit ?? 20),
      offset: String(options.offset ?? 0)
    });
    return apiFetch(
      `/api/v1/games/${gameCode}/predictions/evaluated?${query.toString()}`,
      {},
      predictionEvaluationPageSchema
    );
  },
  evaluationHistory: (
    gameCode: GameCode,
    options: { limit?: number; offset?: number } = {}
  ): Promise<PredictionEvaluationHistoryPage> => {
    const query = new URLSearchParams({
      limit: String(options.limit ?? 20),
      offset: String(options.offset ?? 0)
    });
    return apiFetch(
      `/api/v1/games/${gameCode}/evaluation-history?${query.toString()}`,
      {},
      predictionEvaluationHistoryPageSchema
    );
  }
};

export const accountApi = {
  savedSets: (): Promise<SavedSet[]> =>
    apiFetch("/api/v1/me/saved-sets", {}, z.array(savedSetSchema)),
  saveSet: (input: Omit<SavedSet, "id" | "createdAt">): Promise<SavedSet> =>
    apiFetch(
      "/api/v1/me/saved-sets",
      { method: "POST", body: JSON.stringify(input) },
      savedSetSchema
    ),
  deleteSavedSet: (id: string) =>
    apiFetch<{ ok: boolean }>(
      `/api/v1/me/saved-sets/${id}`,
      { method: "DELETE" },
      z.object({ ok: z.boolean() })
    ),
  notificationPreferences: (): Promise<NotificationPreference[]> =>
    apiFetch(
      "/api/v1/me/notification-preferences",
      {},
      z.array(notificationPreferenceSchema)
    ),
  updateNotificationPreference: (
    preference: NotificationPreference
  ): Promise<NotificationPreference> =>
    apiFetch(
      `/api/v1/me/notification-preferences/${preference.gameCode}`,
      { method: "PUT", body: JSON.stringify(preference) },
      notificationPreferenceSchema
    ),
  updateProfile: (displayName: string) =>
    apiFetch<{ displayName: string }>(
      "/api/v1/me/profile",
      { method: "PATCH", body: JSON.stringify({ displayName }) },
      z.object({ displayName: z.string() })
    ),
  exportData: () => apiFetch<Record<string, unknown>>("/api/v1/me/export"),
  deleteAccount: () =>
    apiFetch<{ ok: boolean }>(
      "/api/v1/me/account",
      { method: "DELETE" },
      z.object({ ok: z.boolean() })
    )
};
